import React, { useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { financialEntryApi } from '../api';
import {
  FINANCIAL_VIEW_BY,
  ITEMS_PER_PAGE,
  getAccountLabel,
  getInstallmentStatusLabel,
  getStatusLabel,
  getCategoryLabel,
  getSubcategoryLabel,
  getRecurrenceLabel,
} from '../constants';
import { formatCalendarDateBR, formatDateTimeBR } from '../financialFormatters';
import {
  FinancialEntriesOutletContext,
  FinancialEntryListRow,
  FinancialEntryListItem,
} from '../types';
import StatusBadge from './StatusBadge';
import ActionsDropdown from './ActionsDropdown';
import ViewFinancialEntryModal from '../ViewFinancialEntryModal';
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../components/StandardPage';
import { OverflowCell } from '../../../../components/OverflowCell';
import { ErrorMessage, MessageContainer } from './style';

type FinancialRowKind = 'entry' | 'payment' | 'installment' | 'item';

interface FinancialTableRow {
  key: string;
  kind: FinancialRowKind;
  virtual?: boolean;
  numero: string | number;
  entryNumber?: number | null;
  createdAt: string | null;
  value: number | null;
  payer: string | null;
  receiver: string | null;
  dreLine: string | null;
  paymentCondition: string | null;
  businessUnit: string | null;
  status: number | null;
  type: number | null;
  category?: number | null;
  subcategory?: number | null;
  description?: string | null;
  linkedUsers?: string | null;
  paymentMethodName?: string | null;
  recurrence?: number | null;
  installmentCount?: number;
  project?: string | null;
  competence?: string | null;
  dueDate: string | null;
  entry: FinancialEntryListRow;
  subRows?: FinancialTableRow[];
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatProjects = (items?: FinancialEntryListItem[] | null) => {
  if (!items?.length) return '—';
  const projs = items.map((i) => i.project).filter(Boolean);
  return projs.length > 0 ? projs.join(', ') : '—';
};

const formatCompetences = (items?: FinancialEntryListItem[] | null) => {
  if (!items?.length) return '—';
  const comps = items
    .map((i) =>
      i.competenceMonth && i.competenceYear
        ? `${i.competenceMonth}/${i.competenceYear}`
        : null,
    )
    .filter(Boolean);
  return comps.length > 0 ? comps.join(', ') : '—';
};

/** Keep dueDate key for table/export; source is competence month/year. */
const competenceToDueDate = (
  month: string | null | undefined,
  year: string | null | undefined,
): string | null => {
  const m = month?.trim().padStart(2, '0').slice(0, 2);
  const y = year?.trim().slice(0, 4);
  if (!m || !y || y.length !== 4) return null;
  return `${y}-${m}-01`;
};

const buildEntryRows = (
  rows: FinancialEntryListRow[],
  viewBy: string,
): FinancialTableRow[] =>
  rows.map((row) => {
    const subRows: FinancialTableRow[] = row.virtual
      ? []
      : viewBy === FINANCIAL_VIEW_BY.ITEM
        ? (row.items ?? []).map((item) => ({
            key: `${viewBy}-${row.id}-item-${item.id}`,
            kind: 'item' as const,
            numero: item.id,
            createdAt: null,
            value: item.competenceValue,
            payer: row.payer,
            receiver: row.receiver,
            dreLine: row.dreLine,
            paymentCondition: row.paymentCondition,
            businessUnit: item.businessUnit,
            status: row.status,
            type: row.type,
            category: row.category,
            subcategory: row.subcategory,
            description: row.description,
            linkedUsers: row.linkedUsers,
            paymentMethodName: row.paymentMethodName,
            recurrence: row.recurrence,
            installmentCount: row.installmentCount ?? row.installments?.length ?? 1,
            project: item.project ?? '—',
            competence:
              item.competenceMonth && item.competenceYear
                ? `${item.competenceMonth}/${item.competenceYear}`
                : '—',
            dueDate: competenceToDueDate(item.competenceMonth, item.competenceYear),
            entry: row,
          }))
        : (row.installments ?? []).map((installment) => ({
            key: `${viewBy}-${row.id}-inst-${installment.id}`,
            kind: 'installment' as const,
            numero: installment.installmentNumber?.trim() || '—',
            createdAt: null,
            value: installment.value,
            payer: row.payer,
            receiver: row.receiver,
            dreLine: row.dreLine,
            paymentCondition: row.paymentCondition,
            businessUnit: row.businessUnit,
            status: installment.status ?? null,
            type: row.type,
            category: row.category,
            subcategory: row.subcategory,
            description: row.description,
            linkedUsers: row.linkedUsers,
            paymentMethodName: row.paymentMethodName,
            recurrence: row.recurrence,
            installmentCount: row.installmentCount ?? row.installments?.length ?? 1,
            project: formatProjects(row.items),
            competence: formatCompetences(row.items),
            dueDate: installment.dueDate,
            entry: row,
          }));

    return {
      key: row.virtual
        ? `${viewBy}-occ-${row.occurrenceKey}`
        : `${viewBy}-${row.id}`,
      kind: 'entry' as const,
      virtual: row.virtual ?? false,
      numero: row.virtual ? '—' : row.id,
      createdAt: row.virtual ? null : row.createdAt ?? null,
      value: row.totalValue,
      payer: row.payer,
      receiver: row.receiver,
      dreLine: row.dreLine,
      paymentCondition: row.paymentCondition,
      businessUnit: row.businessUnit,
      status: row.status,
      type: row.type,
      category: row.category,
      subcategory: row.subcategory,
      description: row.description,
      linkedUsers: row.linkedUsers,
      paymentMethodName: row.paymentMethodName,
      recurrence: row.recurrence,
      installmentCount: row.installmentCount ?? row.installments?.length ?? 1,
      project: formatProjects(row.items),
      competence: formatCompetences(row.items),
      dueDate: row.dueDate,
      entry: row,
      subRows,
    };
  });

const buildPaymentRows = (rows: FinancialEntryListRow[]): FinancialTableRow[] =>
  rows.map((row) => ({
    key: `payment-${row.id}`,
    kind: 'payment' as const,
    numero: row.id,
    entryNumber: row.entryId ?? null,
    createdAt: row.createdAt ?? null,
    value: row.value ?? row.totalValue,
    payer: row.payer,
    receiver: row.receiver,
    dreLine: row.dreLine,
    paymentCondition: row.paymentCondition,
    businessUnit: row.businessUnit,
    status: row.installmentStatus ?? null,
    type: row.type,
    category: row.category,
    subcategory: row.subcategory,
    description: row.description,
    linkedUsers: row.linkedUsers,
    paymentMethodName: row.paymentMethodName,
    recurrence: row.recurrence,
    installmentCount: row.installmentCount ?? row.installments?.length ?? 1,
    project: formatProjects(row.items),
    competence: formatCompetences(row.items),
    dueDate: row.dueDate,
    entry: row,
  }));

const FinancialEntriesTable: React.FC = () => {
  const {
    search,
    filters,
    refreshKey,
    triggerRefresh,
  } = useOutletContext<FinancialEntriesOutletContext>();

  const [viewEntryId, setViewEntryId] = useState<number | null>(null);

  const viewBy = filters.viewBy ?? FINANCIAL_VIEW_BY.ENTRY;
  const isPaymentView = viewBy === FINANCIAL_VIEW_BY.PAYMENT;
  const isItemView = viewBy === FINANCIAL_VIEW_BY.ITEM;

  const queryFilters = useMemo(
    () => ({
      ...filters,
      search: search || undefined,
      limit: ITEMS_PER_PAGE,
      viewBy,
    }),
    [filters, search, viewBy],
  );

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['financialEntries', queryFilters, refreshKey],
    queryFn: ({ pageParam = 1 }) =>
      financialEntryApi.list({ ...queryFilters, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const fetched = lastPage.page * lastPage.limit;
      if (fetched < lastPage.total) return lastPage.page + 1;
      return undefined;
    },
  });

  const rawRows: FinancialEntryListRow[] = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const tableRows = useMemo(
    () => (isPaymentView ? buildPaymentRows(rawRows) : buildEntryRows(rawRows, viewBy)),
    [rawRows, isPaymentView, viewBy],
  );

  const total = data?.pages[0]?.total ?? 0;

  const handleEntryUpdated = () => {
    triggerRefresh();
    refetch();
  };

  const columns = useMemo<StandardColumn<FinancialTableRow>[]>(() => {
    const sharedColumns: StandardColumn<FinancialTableRow>[] = [
      {
        id: 'numero',
        label: 'Número',
        accessor: (row) => row.numero,
      },
      {
        id: 'linkedUsers',
        label: 'Usuário vinculado',
        accessor: (row) => row.linkedUsers ?? '',
        cell: (row) => <OverflowCell text={row.linkedUsers || '—'} maxWidth={160} />,
      },
      {
        id: 'type',
        label: 'Tipo',
        accessor: (row) => getAccountLabel(row.type),
        cell: (row) => <>{getAccountLabel(row.type)}</>,
      },
      {
        id: 'category',
        label: 'Categoria',
        accessor: (row) => getCategoryLabel(row.category),
        cell: (row) => <>{getCategoryLabel(row.category)}</>,
      },
      {
        id: 'subcategory',
        label: 'Subcategoria',
        accessor: (row) => getSubcategoryLabel(row.subcategory),
        cell: (row) => <OverflowCell text={getSubcategoryLabel(row.subcategory)} maxWidth={160} />,
      },
      {
        id: 'description',
        label: 'Descrição',
        accessor: (row) => row.description ?? '',
        cell: (row) => <OverflowCell text={row.description || '—'} maxWidth={200} />,
      },
      {
        id: 'paymentMethod',
        label: 'Forma de pagamento',
        accessor: (row) => row.paymentMethodName ?? '',
        cell: (row) => <OverflowCell text={row.paymentMethodName || '—'} maxWidth={150} />,
      },
      ...(!isItemView
        ? [
            {
              id: 'value',
              label: 'Valor total',
              accessor: (row: FinancialTableRow) => row.value ?? 0,
              cell: (row: FinancialTableRow) => <>{formatCurrency(row.value)}</>,
              exportValue: (row: FinancialTableRow) => formatCurrency(row.value),
            } satisfies StandardColumn<FinancialTableRow>,
          ]
        : [
            {
              id: 'value',
              label: 'Valor',
              accessor: (row: FinancialTableRow) => row.value ?? 0,
              cell: (row: FinancialTableRow) => <>{formatCurrency(row.value)}</>,
              exportValue: (row: FinancialTableRow) => formatCurrency(row.value),
            } satisfies StandardColumn<FinancialTableRow>,
          ]),
      {
        id: 'recurrence',
        label: 'Recorrência',
        accessor: (row) => getRecurrenceLabel(row.recurrence),
        cell: (row) => <>{getRecurrenceLabel(row.recurrence)}</>,
      },
      {
        id: 'installments',
        label: 'Parcelas',
        accessor: (row) => (row.installmentCount ? `${row.installmentCount}x` : '1x'),
        cell: (row) => <>{row.installmentCount ? `${row.installmentCount}x` : '1x'}</>,
      },
      {
        id: 'project',
        label: 'Projeto',
        accessor: (row) => row.project ?? '',
        cell: (row) => <OverflowCell text={row.project || '—'} maxWidth={160} />,
      },
      {
        id: 'competence',
        label: 'Competência',
        accessor: (row) => row.competence ?? '',
        cell: (row) => <OverflowCell text={row.competence || '—'} maxWidth={140} />,
      },
      {
        id: 'payer',
        label: 'Pagador',
        accessor: (row) => row.payer ?? '',
        cell: (row) => <OverflowCell text={row.payer || '—'} maxWidth={160} />,
      },
      {
        id: 'receiver',
        label: 'Recebedor',
        accessor: (row) => row.receiver ?? '',
        cell: (row) => <OverflowCell text={row.receiver || '—'} maxWidth={160} />,
      },
      {
        id: 'dreLine',
        label: 'Linha do DRE',
        accessor: (row) => row.dreLine ?? '',
        cell: (row) => <OverflowCell text={row.dreLine || '—'} maxWidth={160} />,
      },
      {
        id: 'paymentCondition',
        label: 'Condição',
        accessor: (row) => row.paymentCondition ?? '',
        cell: (row) => (
          <OverflowCell text={row.paymentCondition || '—'} maxWidth={120} />
        ),
      },
      {
        id: 'businessUnit',
        label: 'BU',
        accessor: (row) => row.businessUnit ?? '',
        cell: (row) => <>{row.businessUnit ?? '—'}</>,
      },
      {
        id: 'status',
        label: 'Status',
        accessor: (row) =>
          row.virtual
            ? 'Previsto'
            : row.kind === 'installment' || row.kind === 'payment'
              ? getInstallmentStatusLabel(row.status)
              : getStatusLabel(row.status),
        cell: (row) => (
          <StatusBadge
            status={row.status}
            virtual={row.virtual}
            variant={
              row.kind === 'installment' || row.kind === 'payment'
                ? 'installment'
                : 'entry'
            }
          />
        ),
        exportValue: (row) =>
          row.virtual
            ? 'Previsto'
            : row.kind === 'installment' || row.kind === 'payment'
              ? getInstallmentStatusLabel(row.status)
              : getStatusLabel(row.status),
      },
      {
        id: 'dueDate',
        label: 'Vencimento',
        accessor: (row) => row.dueDate ?? '',
        cell: (row) => <>{formatCalendarDateBR(row.dueDate)}</>,
        exportValue: (row) => formatCalendarDateBR(row.dueDate),
      },
      {
        id: 'createdAt',
        label: 'Registro',
        accessor: (row) => row.createdAt ?? '',
        cell: (row) => <>{row.createdAt ? formatDateTimeBR(row.createdAt) : '—'}</>,
        exportValue: (row) => (row.createdAt ? formatDateTimeBR(row.createdAt) : '—'),
      },
    ];

    if (isPaymentView) {
      return [
        {
          id: 'entryNumber',
          label: 'Número do Lançamento',
          accessor: (row) => row.entryNumber ?? '',
          cell: (row) => <>{row.entryNumber ?? '—'}</>,
        },
        ...sharedColumns,
      ];
    }

    return sharedColumns;
  }, [isPaymentView, isItemView]);

  const screenKey = `financial_entries_${viewBy}`;
  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(screenKey, columns);

  if (isError) {
    return (
      <ErrorMessage>
        Erro ao carregar lançamentos: {(error as Error)?.message ?? 'Tente novamente.'}
      </ErrorMessage>
    );
  }

  return (
    <>
      <StandardDataTable
        columns={columns}
        data={tableRows}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        countLabel="lançamentos"
        totalCount={total}
        hasMore={!!hasNextPage}
        isLoading={isPending || isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        exportFileName={`financeiro-${viewBy}`}
        emptyMessage="Nenhum lançamento encontrado."
        getSubRows={(row) => row.subRows}
        getRowId={(row) => row.key}
        expandColumnId="numero"
        renderActions={(row) =>
          row.kind === 'entry' || row.kind === 'payment' ? (
            <div onClick={(event) => event.stopPropagation()}>
              <ActionsDropdown
                entry={row.entry}
                onView={(entryId) => setViewEntryId(entryId)}
                onUpdated={handleEntryUpdated}
              />
            </div>
          ) : null
        }
      />

      {viewEntryId !== null && (
        <ViewFinancialEntryModal
          entryId={viewEntryId}
          onClose={() => setViewEntryId(null)}
          onUpdated={handleEntryUpdated}
        />
      )}
    </>
  );
};

export default FinancialEntriesTable;
